import { renderIcon } from "Components/sde-e-icon/sde-e-icon.template";
import { renderCssClasses } from "Helper/renderHelper/renderCssClasses";
import { html, nothing } from "lit-html";
import { ifDefined } from "lit-html/directives/if-defined.js";

import { LazyImage } from "./sde-e-lazy-image";
import styles from "./sde-e-lazy-image.shadow.scss";
import { RenderLazyImageArgs } from "./sde-e-lazy-image.types";

export interface LazyImageTemplateArgs {
  src: string;
  srcset?: string;
  sizes?: string;
  alt?: string;
  aspectRatio?: string;
  component: LazyImage;
  handlePlaceholderLoad: () => void;
  handlePlaceholderError: () => void;
}

const shouldRenderErrorPlaceholder = (args: LazyImageTemplateArgs) => {
  return !(args.component.hasError === false || args.component.loadingPlaceholderLoaded === true);
};

const renderErrorPlaceholder = (args: LazyImageTemplateArgs, forceRender = false) => {
  if (forceRender === false && !shouldRenderErrorPlaceholder(args)) return "";

  return html`
    <div class="sde-e-lazy-image__error-placeholder-box">
      ${renderIcon({
        iconId: "sde-icon-logo-quad",
        cssClasses: "sde-e-lazy-image__error-placeholder-icon",
      })}
    </div>
  `;
};

const renderPlaceholder = (args: LazyImageTemplateArgs) => {
  if (args.component.noPlaceholder === true) return nothing;
  if (args.component.loadingPlaceholder === "" || args.component.loadingPlaceholderLoadingError === true) {
    return renderErrorPlaceholder(args, true);
  }

  return html`
    <img
      draggable="false"
      class="sde-e-lazy-image__placeholder"
      src="${args.component.loadingPlaceholder}"
      alt="${ifDefined(args.alt)}"
      @load=${args.handlePlaceholderLoad}
      @error=${args.handlePlaceholderError}
    />
  `;
};

const renderImage = (args: LazyImageTemplateArgs) => {
  return html`
    <img
      draggable="false"
      class="sde-e-lazy-image__image"
      src="${args.src}"
      srcset="${ifDefined(args.srcset)}"
      alt="${ifDefined(args.alt)}"
      sizes="${ifDefined(args.sizes)}"
    />
  `;
};

export const lazyImageTemplate = (args: LazyImageTemplateArgs) => {
  return html`
    <style>
      ${styles}
    </style>
    <div
      class="sde-e-lazy-image__image-box"
      data-aspect-ratio="${ifDefined(args.aspectRatio)}"
      ?data-image-loaded="${args.component.imageLoaded}"
    >
      ${renderPlaceholder(args)} ${renderImage(args)}
    </div>
  `;
};

export const renderLazyImage = (args: RenderLazyImageArgs) => {
  return html`
    <sde-e-lazy-image
      class="sde-e-lazy-image${renderCssClasses(args.cssClasses)}"
      src="${args.src}"
      alt=${ifDefined(args.alt)}
      srcset=${ifDefined(args.srcset)}
      sizes=${ifDefined(args.sizes)}
      ?no-placeholder=${args.noPlaceholder ?? false}
      loading-placeholder=${ifDefined(args.loadingPlaceholder)}
      aspect-ratio=${ifDefined(args.aspectRatio)}
    >
    </sde-e-lazy-image>
  `;
};

export interface RenderLazyImagePlaceholderArgs {
  cssClasses?: string;
  aspectRatio: string;
}

export const renderLazyImagePlaceholder = (args: RenderLazyImagePlaceholderArgs) => {
  return html`
    <div
      class="sde-e-lazy-image sde-e-lazy-image--placeholder${renderCssClasses(args.cssClasses)}"
      aspect-ratio="${args.aspectRatio}"
    >
      <div class="sde-e-lazy-image__placeholder-content">
        ${renderIcon({
          cssClasses: "sde-e-lazy-image__placeholder-icon",
          iconId: "sde-icon-image",
        })}
      </div>
    </div>
  `;
};
